import React, { useCallback, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { IconButton } from '@atlaskit/button/new';
import {
	SpotlightManager,
	SpotlightTarget,
	SpotlightTransition,
	SpotlightPulse,
} from '@atlaskit/onboarding';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { Box } from '@atlaskit/primitives';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getProjectName, projectIdSelector } from '../../../state/selectors/app/index.tsx';
import { useCoUseFlow } from '../co-use-flow/index.tsx';
import {
	USER_PROPERTY_KEY_NAMES,
	CTA_SOURCE_CONTEXT,
	CTA_SOURCE_COMPONENT,
	type Flow,
} from '../constants.tsx';
import { onClickCTA } from '../on-click-cta/utils.tsx';
import { useIsBacklogCardListSizeGreaterThanTwo } from '../utils.tsx';
import messages from '../messages.tsx';
import { AdControls } from './ad-controls/view.tsx';
import { useAdControls } from './ad-controls/controller.tsx';

type BacklogButtonRefreshProps = {
	flow: Flow;
	hoverPulse: boolean;
};

export const BacklogButtonRefresh = ({ flow, hoverPulse }: BacklogButtonRefreshProps) => {
	const coUseEligible = flow === 'coUse';
	const crossFlowEligible = flow === 'crossFlow';
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const crossflow = useCrossFlow();
	const { hasDismissed, dismiss } = useAdControls();

	const url = useCoUseFlow(coUseEligible);

	// Extract project name and project id
	const projectName = useBacklogSelector(getProjectName);
	const projectId = useBacklogSelector(projectIdSelector);

	const onPlanOnWhiteboard = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			CTA_SOURCE_CONTEXT,
			{
				sourceComponent: CTA_SOURCE_COMPONENT,
				sourceContext: CTA_SOURCE_CONTEXT,
				experiment: 'backlogButtonRefresh',
			},
		);

		onClickCTA({
			createAnalyticsEvent,
			crossFlowEligible,
			crossflow,
			projectId,
			projectName,
			component: CTA_SOURCE_COMPONENT,
			context: CTA_SOURCE_CONTEXT,
			coUseEligible,
			url,
		});
	}, [
		crossFlowEligible,
		coUseEligible,
		projectId,
		projectName,
		crossflow,
		createAnalyticsEvent,
		url,
	]);

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const handleDismissEntrypoint = useCallback(async () => {
		await dismiss(USER_PROPERTY_KEY_NAMES.WHITEBOARDS_BACKLOG_ENTRYPOINT_DISMISSED);
	}, [dismiss]);

	const openSpotlight = useCallback(() => {
		setIsSpotlightActive(true);
	}, []);

	const closeSpotlight = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const shouldRenderPulse =
		(useIsBacklogCardListSizeGreaterThanTwo() && coUseEligible && !isSpotlightActive) ||
		(crossFlowEligible && hoverPulse);

	if (hasDismissed) return null;

	return (
		<>
			<Box>
				<SpotlightManager blanketIsTinted={false} onBlanketClicked={closeSpotlight}>
					<SpotlightTarget name="whiteboards">
						<SpotlightPulse
							radius={3}
							pulse={shouldRenderPulse}
							testId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.pulse"
						>
							<IconButton
								icon={WhiteboardIcon}
								onClick={onPlanOnWhiteboard}
								spacing="compact"
								label={formatMessage(messages.planOnWhiteboard)}
								onMouseEnter={openSpotlight}
							/>
						</SpotlightPulse>
					</SpotlightTarget>

					<SpotlightTransition>
						{isSpotlightActive && (
							<JiraSpotlight
								heading={formatMessage(messages.spotlightHeading)}
								actions={[
									{
										onClick: onPlanOnWhiteboard,
										text: formatMessage(messages.spotlightCta),
									},
								]}
								actionsBeforeElement={<AdControls isCta onDismissAd={handleDismissEntrypoint} />}
								target="whiteboards"
								targetRadius={3}
								dialogWidth={320}
								dialogPlacement="bottom right"
								pulse={false}
								targetOnClick={onPlanOnWhiteboard}
								messageType="engagement"
								messageId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.jira-spotlight.whiteboards"
								testId="software-backlog.whiteboards-entrypoint-experiment.backlog-button-refresh.whiteboards-spotlight"
							>
								{formatMessage(messages.spotlightContent)}
							</JiraSpotlight>
						)}
					</SpotlightTransition>
				</SpotlightManager>
			</Box>
			<FireScreenAnalytics
				attributes={{ crossFlowEligible, coUseEligible, experiment: 'backlogButtonRefresh' }}
			/>
		</>
	);
};
